import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileBarChart2, Plus } from "lucide-react";
import { api } from "@/api/client";
import type { Report, RollupReportSummary, RollupSchedule, SnapshotReportSummary } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/ui/error-state";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";

// One row shape for all three artifact types — the flavor is a chip, not a page.
interface HubRow {
  key: string;
  kind: "Narrative" | "Recap" | "Snapshot";
  name: string;
  sub: string;
  status: string | null;
  created_at: string;
  publicPath: string;
  appPath: string | null;
}

const KIND_VARIANT = { Narrative: "default", Recap: "muted", Snapshot: "category" } as const;

function fmtDate(sql: string): string {
  const d = new Date(sql.replace(" ", "T") + "Z");
  return isNaN(d.getTime()) ? sql : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function toRows(reports: Report[], rollups: RollupReportSummary[], snapshots: SnapshotReportSummary[]): HubRow[] {
  const rows: HubRow[] = [
    ...reports.map((r): HubRow => ({
      key: `n-${r.id}`,
      kind: "Narrative",
      name: r.name,
      sub: `${r.models.length} model${r.models.length === 1 ? "" : "s"} · ${r.runs_per_model} run${r.runs_per_model === 1 ? "" : "s"} each`,
      status: r.status,
      created_at: r.created_at,
      publicPath: `/reports/${r.slug}`,
      appPath: `/app/reports/${r.id}`,
    })),
    ...rollups.map((r): HubRow => ({
      key: `r-${r.id}`,
      kind: "Recap",
      name: r.name,
      sub: r.model ? `${r.period_type} · ${r.model}` : `${r.period_type} · all models`,
      status: r.status,
      created_at: r.created_at,
      publicPath: `/rollups/${r.slug}`,
      appPath: `/app/rollups/${r.id}`,
    })),
    ...snapshots.map((s): HubRow => ({
      key: `s-${s.id}`,
      kind: "Snapshot",
      name: s.name,
      sub: `Brand Health freeze · ${s.period_label}`,
      status: null,
      created_at: s.created_at,
      publicPath: `/snapshots/${s.slug}`,
      appPath: null,
    })),
  ];
  return rows.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

export function ReportsHubView() {
  const [rows, setRows] = useState<HubRow[] | null>(null);
  const [schedules, setSchedules] = useState<RollupSchedule[]>([]);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError("");
    Promise.all([
      api.reports.list(),
      api.rollups.list(),
      api.snapshots.list(),
      // Auto-recap config is decoration here — don't fail the page over it.
      api.rollups.autoSchedules().catch(() => [] as RollupSchedule[]),
    ])
      .then(([reports, rollups, snapshots, auto]) => {
        if (cancelled) return;
        setRows(toRows(reports, rollups, snapshots));
        setSchedules(auto);
      })
      .catch((err) => { if (!cancelled) setError(err?.message || "Failed to load reports"); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (error) return <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  if (!rows) return <Spinner message="Loading reports…" size="lg" />;

  const autoOn = schedules.filter((s) => s.enabled).map((s) => s.period_type);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {(["month", "quarter", "year"] as const).map((p) => (
            <Link key={p} to="/app/rollups" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[12px] font-medium text-slate-500 hover:border-brand-100 hover:text-brand-600">
              {p === "month" ? "Monthly" : p === "quarter" ? "Quarterly" : "Yearly"} recap ·{" "}
              <span className={autoOn.includes(p) ? "font-bold text-green-600" : "font-semibold"}>
                {autoOn.includes(p) ? "on" : "off"}
              </span>
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/app/health"><Button variant="secondary">Freeze a snapshot</Button></Link>
          <Link to="/app/reports/narrative">
            <Button><Plus className="w-4 h-4 mr-1.5" />New narrative report</Button>
          </Link>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={FileBarChart2}
          title="No reports yet"
          description="Reports are frozen, shareable artifacts: automated monthly recaps, leadership snapshots frozen from Brand Health, and AI-written narrative reports."
          action={
            <div className="flex items-center gap-3">
              <Link to="/app/reports/narrative"><Button>Create a narrative report</Button></Link>
              <Link to="/app/rollups"><Button variant="secondary">Turn on monthly recaps</Button></Link>
            </div>
          }
        />
      ) : (
        <Card>
          <CardContent className="divide-y divide-slate-100">
            {rows.map((row) => (
              <div key={row.key} className="flex items-center gap-3 py-3 first:pt-1 last:pb-1">
                <Badge variant={KIND_VARIANT[row.kind]}>{row.kind}</Badge>
                <div className="min-w-0 flex-1">
                  {row.appPath ? (
                    <Link to={row.appPath} className="block text-[13px] font-semibold text-brand-950 truncate hover:text-brand-600">
                      {row.name}
                    </Link>
                  ) : (
                    <span className="block text-[13px] font-semibold text-brand-950 truncate">{row.name}</span>
                  )}
                  <span className="block text-[11.5px] text-slate-400">{row.sub}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {row.status && row.status !== "completed" && <StatusBadge status={row.status} />}
                  <span className="text-[11.5px] text-slate-400 tabular-nums">{fmtDate(row.created_at)}</span>
                  <a
                    href={row.publicPath}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-600 hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Public link
                  </a>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <p className="text-[11.5px] text-slate-400 text-center">
        Every report is frozen at creation — a link you share today reads the same in six months.
      </p>
    </div>
  );
}
